import { keycloakErrors } from '@/constants/keycloak';
import { AuthenticationStatus } from '@/enums/authentication-status';
import { useIsMobile } from '@/hooks/is-mobile';
import { useClearCache } from '@/hooks/shared/clear-cache';
import { useClearCart } from '@/hooks/shopping-cart/shopping-cart';
import { refreshSharedState, useSharedState, useUpdateSharedState } from '@/hooks/state/shared-state';
import { useAuth } from '@/hooks/use-auth';
import { authenticateWithMobileToken } from '@/services/(backend)/auth-mobile';
import { mobileActionRoutes, mobileRoutes } from '@/utils/mobile';
import { signIn, signOut, useSession } from 'next-auth/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { MobileAuthGuardComponent } from './mobile-auth-guard';

const authenticatedRoutes = ['/account', mobileRoutes.accountProfileMovieClubPayment, mobileRoutes.accountProfileSavedPayment];
const cooldownPeriodMs = 10000;

type AuthenticationProviderProps = {
  children: React.ReactNode;
  mobileBearerToken?: string;
};

export const AuthenticationProvider = ({ children, mobileBearerToken }: Readonly<AuthenticationProviderProps>): React.JSX.Element => {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: sharedState } = useSharedState();
  const { mutateAsync: updateSharedState } = useUpdateSharedState();
  const { signIn: legacySignIn } = useAuth();
  const { cleanupWebViewCloseDetection, initializeWebViewCloseDetection, isMobile, mobileAuthStatus, setMobileAuthStatus } = useIsMobile();
  const [previousPathname, setPreviousPathname] = useState<string | null>();
  const [lastSignOutAttempt, setLastSignOutAttempt] = useState<number>(0);
  const [lastSignInAttempt, setLastSignInAttempt] = useState<number>(0);
  const clearCache = useClearCache();
  const { mutateAsync: clearCart } = useClearCart();

  const handleSignout = useCallback(async (redirectUrl?: string): Promise<void> => {
    // Check if we recently attempted a sign-out to prevent infinite loops when the state changes rapidly
    // This prevents the case where we return from successful Keycloak auth
    // but legacy hasn't set the shared state cookie yet
    const now = Date.now();
    const recentSignOut = now - lastSignOutAttempt < cooldownPeriodMs;
    if (recentSignOut) {
      return;
    }

    setLastSignOutAttempt(now);
    try {
      if (sharedState?.cartItemCount) {
        await clearCart();
      }
    } catch {
      // eat error
    } finally {
      await updateSharedState({ cartItemCount: 0, guestCustomerId: null, shoppingCartId: null });
    }

    clearCache();
    setMobileAuthStatus({ needsAuthentication: true, status: 'signing-out' });
    updateSharedState({ isAuthenticated: false });
    await signOut({ redirect: true, redirectTo: redirectUrl || '/' });
  // We are intentionally not adding lastSignOutAttempt so the callback doesn't get recreated every time a sign-out attempt is made
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearCache, clearCart, setMobileAuthStatus, sharedState?.cartItemCount, updateSharedState]);

  const handleSignIn = useCallback(async (provider: 'cinemark' | 'legacy'): Promise<void> => {
    // Check if we recently attempted a sign-in to prevent multiple calls when the state changes rapidly
    const now = Date.now();
    const recentSignIn = now - lastSignInAttempt < cooldownPeriodMs;
    if (recentSignIn) {
      return;
    }

    setLastSignInAttempt(now);

    if (provider === 'legacy') {
      legacySignIn();
    } else {
      await signIn('cinemark');
    }
  // We are intentionally not adding lastSignInAttempt so the callback doesn't get recreated every time a sign-out attempt is made
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacySignIn]);

  const handleWebAuthentication = useCallback(async (isAuthenticatedRoute: boolean): Promise<void> => {
    if (session?.error && (isAuthenticatedRoute || session.user)) {
      if (session.error === keycloakErrors.noRefreshTokenError) {
        // this is a mobile issue where we don't have a refresh token, so we need to sign out
        await handleSignout();
        return;
      } else if (session.error === keycloakErrors.sessionInvalid) {
        // Session has become invalid, sign out everywhere
        await handleSignout();
        return;
      }

      const refreshedState = await refreshSharedState();
      if (refreshedState.isAuthenticated) {
        // problem with session, redirect to keycloak
        await handleSignIn('cinemark');
      } else {
        // no longer authenticated, sign out everywhere
        await handleSignout();
      }

      return;
    }

    if (!session?.keycloakId && (sharedState?.isAuthenticated === true || isAuthenticatedRoute)) {
      if (sharedState?.isAuthenticated === true) {
        const refreshedState = await refreshSharedState();

        if (refreshedState.isAuthenticated !== true) {
          // legacy website is not authenticated, no need to redirect to sign in
          // clear cache before reload to get a clean state without a session/profile
          clearCache();
          window.location.reload();
          return;
        }
      }

      if (sharedState?.isAuthenticated !== true && isAuthenticatedRoute) {
        await handleSignIn('legacy');
        setMobileAuthStatus({ needsAuthentication: true, status: 'loading' });
        return;
      }

      // get cookie from keycloak
      // session missing and legacy website authenticated
      await handleSignIn('cinemark');
      return;
    }

    if (session && sharedState?.isAuthenticated !== true) {
      const refreshedState = await refreshSharedState();

      if (refreshedState.isAuthenticated === true) {
        // Legacy website is authenticated, no need to redirect to sign out
        // clear cache before reload to get a clean state with a session/profile
        clearCache();
        window.location.reload();
        return;
      }

      // Session present and legacy website is not authenticated
      await handleSignout();
    }
  }, [clearCache, handleSignout, handleSignIn, session, setMobileAuthStatus, sharedState?.isAuthenticated]);

  const handleMobileWebViewAuthentication = useCallback(async (isAuthenticatedRoute: boolean): Promise<void> => {
    const isNewPath = previousPathname === null || previousPathname !== pathname;
    setPreviousPathname(pathname);

    if (mobileAuthStatus.status !== 'loading' && !isNewPath || mobileAuthStatus.status === 'signing-out') return;

    const currentUrl = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
    const refreshAccessTokenUrl = `${mobileActionRoutes.refreshAccessToken}?returnUrl=${encodeURIComponent(currentUrl)}`;

    if (isAuthenticatedRoute) {
      if (!mobileBearerToken) {
        setMobileAuthStatus({ needsAuthentication: true, status: 'authenticating' });
        await handleWebAuthentication(isAuthenticatedRoute);
        return;
      }

      if (!session) {
        setMobileAuthStatus({ needsAuthentication: true, status: 'authenticating' });

        const result = await authenticateWithMobileToken(mobileBearerToken);
        if (result.success) {
          setMobileAuthStatus({ needsAuthentication: false, status: 'authenticated' });
          await updateSharedState({ isAuthenticated: true });
          return;
        }

        await handleSignout(refreshAccessTokenUrl);
        return;
      } else if (session.error === keycloakErrors.noRefreshTokenError) {
        await handleSignout(refreshAccessTokenUrl);
        return;
      } else if (session.error === keycloakErrors.sessionInvalid) {
        // Session has become invalid, sign out everywhere
        await handleSignout();
        return;
      }

      setMobileAuthStatus({ needsAuthentication: false, status: 'authenticated' });
      updateSharedState({ isAuthenticated: true });
      return;
    } else if (mobileBearerToken) {
      setMobileAuthStatus({ needsAuthentication: true, status: 'authenticating' });
      const result = await authenticateWithMobileToken(mobileBearerToken);

      if (result.success) {
        setMobileAuthStatus({ needsAuthentication: false, status: 'authenticated' });
        await updateSharedState({ isAuthenticated: true });
        return;
      }

      await handleSignout(refreshAccessTokenUrl);
      return;
    } else if (session?.error === keycloakErrors.noRefreshTokenError) {
      // this is a mobile issue where we don't have a refresh token, so we need to sign out
      await handleSignout(refreshAccessTokenUrl);
      return;
    } else if (session?.error === keycloakErrors.sessionInvalid) {
      // Session has become invalid, sign out everywhere
      await handleSignout();
      return;
    }

    setMobileAuthStatus({ needsAuthentication: false, status: 'unauthenticated' });
  }, [handleSignout, handleWebAuthentication, mobileAuthStatus.status, mobileBearerToken, pathname, previousPathname,
    searchParams, session, setMobileAuthStatus, updateSharedState]);

  useEffect(() => {
    if (isMobile && mobileBearerToken) {
      initializeWebViewCloseDetection();
    }

    return (): void => {
      cleanupWebViewCloseDetection();
    };
  }, [cleanupWebViewCloseDetection, initializeWebViewCloseDetection, isMobile, mobileBearerToken]);

  useEffect(() => {
    const abortController = new AbortController();
    const isAuthenticatedRoute = authenticatedRoutes.some(route => pathname === route || pathname.startsWith(`${route}/`));

    const checkAuthentication = async (): Promise<void> => {
      try {
        if (status === AuthenticationStatus.LOADING) return;

        if (!abortController.signal.aborted) {
          if (isMobile) {
            await handleMobileWebViewAuthentication(isAuthenticatedRoute);
          } else {
            await handleWebAuthentication(isAuthenticatedRoute);
          }
        }
      } catch {
        // eat error
      }
    };

    checkAuthentication();

    return (): void => {
      abortController.abort();
    };
  }, [handleMobileWebViewAuthentication, handleWebAuthentication, isMobile, pathname, status]);

  if (isMobile && mobileAuthStatus?.needsAuthentication && (!session?.keycloakId || !sharedState?.isAuthenticated)) {
    return <MobileAuthGuardComponent status={mobileAuthStatus.status} />;
  }

  return <>{children}</>;
};
